<admin-app-header></admin-app-header>

<div class="admyt-users-page">
    <div class="container-fluid">
        <!-- Page Header -->
        <div class="page-header">
            <h1>Admyt Users</h1>
            <div class="page-header__actions">
                <button class="btn btn-secondary" (click)="refreshData()">
                    <i class="fa-solid fa-refresh"></i> Refresh
                </button>
            </div>
        </div>

        <!-- Summary Stats -->
        <div class="stats-grid" *ngIf="!loading && !error">
            <div class="stat-card stat-card--blue">
                <span class="data-source data-source--admyt"><i class="fa-solid fa-cloud"></i> Admyt</span>
                <div class="stat-card__icon">
                    <i class="fa-solid fa-users"></i>
                </div>
                <div class="stat-card__content">
                    <span class="stat-card__label">Total Users</span>
                    <span class="stat-card__value">{{ users.length }}</span>
                </div>
            </div>
            <div class="stat-card stat-card--green">
                <span class="data-source data-source--admyt"><i class="fa-solid fa-cloud"></i> Admyt</span>
                <div class="stat-card__icon">
                    <i class="fa-solid fa-user-check"></i>
                </div>
                <div class="stat-card__content">
                    <span class="stat-card__label">Active</span>
                    <span class="stat-card__value">{{ getActiveCount() }}</span>
                </div>
            </div>
            <div class="stat-card stat-card--orange">
                <span class="data-source data-source--admyt"><i class="fa-solid fa-cloud"></i> Admyt</span>
                <div class="stat-card__icon">
                    <i class="fa-solid fa-car"></i>
                </div>
                <div class="stat-card__content">
                    <span class="stat-card__label">Total Cars</span>
                    <span class="stat-card__value">{{ getTotalCars() }}</span>
                </div>
            </div>
            <div class="stat-card stat-card--purple">
                <div class="stat-card__icon">
                    <i class="fa-solid fa-filter"></i>
                </div>
                <div class="stat-card__content">
                    <span class="stat-card__label">Filtered Results</span>
                    <span class="stat-card__value">{{ filteredUsers.length }}</span>
                </div>
            </div>
        </div>

        <!-- Filter Bar -->
        <div class="filter-bar" *ngIf="!loading && !error">
            <div class="filter-bar__left">
                <div class="filter-search">
                    <i class="fa-solid fa-magnifying-glass"></i>
                    <input type="text" 
                           class="filter-input" 
                           placeholder="Search by name, email, or ID..." 
                           [(ngModel)]="searchTerm" 
                           (input)="onSearchChange()" />
                </div>
                <div class="status-toggle">
                    <button *ngFor="let statusOption of availableStatuses" 
                            class="toggle-btn" 
                            [class.active]="statusFilter === statusOption" 
                            (click)="statusFilter = statusOption; onStatusFilterChange()">
                        {{ statusOption | titlecase }}
                    </button>
                </div>
            </div>
            <div class="filter-bar__right">
                <select class="items-select" [(ngModel)]="itemsPerPage" (change)="onSearchChange()">
                    <option value="10">10 per page</option>
                    <option value="20">20 per page</option>
                    <option value="50">50 per page</option>
                    <option value="100">100 per page</option>
                </select>
                <span class="results-count">{{ filteredUsers.length }} results</span>
            </div>
        </div>

        <!-- Loading State -->
        <div class="loading-state" *ngIf="loading">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <p>Loading users...</p>
        </div>

        <!-- Error State -->
        <div class="error-state" *ngIf="error">
            <i class="fa-solid fa-exclamation-triangle"></i>
            <h3>Error Loading Users</h3>
            <p>{{ error }}</p>
            <button class="btn btn-primary" (click)="refreshData()">
                <i class="fa-solid fa-refresh"></i> Try Again
            </button>
        </div>

        <!-- Empty State -->
        <div class="empty-state" *ngIf="!loading && !error && filteredUsers.length === 0">
            <i class="fa-solid fa-users"></i>
            <h3>No Users Found</h3>
            <p *ngIf="searchTerm || statusFilter !== 'all'">Try adjusting your search criteria or filters.</p>
            <p *ngIf="!searchTerm && statusFilter === 'all'">No users are currently registered in the Admyt system.</p>
        </div>

        <!-- Users Table -->
        <div class="table-card" *ngIf="!loading && !error && filteredUsers.length > 0">
            <table class="users-table">
                <thead>
                    <tr>
                        <th>User ID <span class="data-source data-source--admyt"><i class="fa-solid fa-cloud"></i></span></th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>ParkUpp Client <span class="data-source data-source--parkupp"><i class="fa-solid fa-database"></i></span></th>
                        <th>Status</th>
                        <th>Cars</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let user of getPaginatedUsers()" class="user-row">
                        <td>
                            <code class="user-id">{{ truncate(user.id, 12) }}</code>
                        </td>
                        <td>
                            <span class="user-name">{{ getFullName(user) }}</span>
                        </td>
                        <td>
                            {{ user.email }}
                        </td>
                        <td>
                            <a *ngIf="getLinkedClient(user.email) as linkedClient" 
                               [routerLink]="['/admin/client', linkedClient.uid]" 
                               class="admyt-link">
                                <i class="fa-solid fa-user"></i>
                                {{ linkedClient.firstName }} {{ linkedClient.lastName }}
                            </a>
                            <span *ngIf="!getLinkedClient(user.email)" class="text-muted">
                                <i class="fa-solid fa-unlink"></i> Not linked
                            </span>
                        </td>
                        <td>
                            <span class="status-badge" 
                                  [class.status-badge--active]="user.status === 'active'"
                                  [class.status-badge--inactive]="user.status === 'inactive'"
                                  [class.status-badge--pending]="user.status === 'pending'"
                                  *ngIf="user.status">
                                {{ user.status | titlecase }}
                            </span>
                            <span *ngIf="!user.status" class="text-muted">N/A</span>
                        </td>
                        <td>
                            <span *ngIf="getCarCount(user) > 0" class="count-badge">
                                <i class="fa-solid fa-car"></i> {{ getCarCount(user) }}
                            </span>
                            <span *ngIf="getCarCount(user) === 0" class="text-muted">None</span>
                        </td>
                        <td>
                            <div class="action-buttons">
                                <a [routerLink]="['/admin/admyt-user', user.id]" class="btn btn-primary btn-sm">
                                    <i class="fa-solid fa-eye"></i>
                                </a>
                                <a *ngIf="getLinkedClient(user.email)" 
                                   [routerLink]="['/admin/client', getLinkedClient(user.email)?.uid]" 
                                   class="btn btn-secondary btn-sm"
                                   title="View ParkUpp profile">
                                    <i class="fa-solid fa-user"></i>
                                </a>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper" *ngIf="!loading && !error && filteredUsers.length > 0 && totalPages > 1">
            <div class="pagination-info">
                Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage * itemsPerPage, totalItems) }} of {{ totalItems }} users
            </div>
            <div class="pagination">
                <button class="page-btn" (click)="goToPage(1)" [disabled]="currentPage === 1">
                    <i class="fa-solid fa-angles-left"></i>
                </button>
                <button class="page-btn" (click)="goToPage(currentPage - 1)" [disabled]="currentPage === 1">
                    <i class="fa-solid fa-chevron-left"></i>
                </button>
                <button *ngFor="let page of getPaginationPages()" 
                        class="page-btn" 
                        [class.active]="page === currentPage" 
                        (click)="goToPage(page)">
                    {{ page }}
                </button>
                <button class="page-btn" (click)="goToPage(currentPage + 1)" [disabled]="currentPage === totalPages">
                    <i class="fa-solid fa-chevron-right"></i>
                </button>
                <button class="page-btn" (click)="goToPage(totalPages)" [disabled]="currentPage === totalPages">
                    <i class="fa-solid fa-angles-right"></i>
                </button>
            </div>
        </div>
    </div>
</div>
